import React, { useMemo, useState } from "react";
import classnames from "classnames";

import { getDateObject, getDateString } from "../../helpers/dates";

import DatePicker from "../DatePicker";
import SelectInput from "../SelectInput";
import TextInput from "../TextInput";

import styles from "./DateTimePicker.module.scss";

const HOURS_IN_HALF_DAY = 12;
const MAX_MINUTE = 59;

const MERIDIEM_AM = "AM";
const MERIDIEM_PM = "PM";

const MERIDIEM_OPTIONS = [
  { value: MERIDIEM_AM, label: MERIDIEM_AM },
  { value: MERIDIEM_PM, label: MERIDIEM_PM },
];

// "99:99" — react-input-mask digit placeholders, so only digits are accepted and
// the colon is fixed. Any minute is reachable in four keystrokes.
const TIME_MASK = "99:99";

const pad = (value: number) => `${value}`.padStart(2, "0");

const to24Hour = (hour: number, meridiem: string) => {
  if (meridiem === MERIDIEM_PM) {
    return hour === HOURS_IN_HALF_DAY
      ? HOURS_IN_HALF_DAY
      : hour + HOURS_IN_HALF_DAY;
  }

  return hour === HOURS_IN_HALF_DAY ? 0 : hour;
};

interface TimeParts {
  hour: number;
  minute: number;
  meridiem: string;
}

const formatTimeText = (time: TimeParts) =>
  `${pad(time.hour)}:${pad(time.minute)}`;

const parseTimeText = (text: string) => {
  const match = text.match(/^(\d{1,2}):(\d{2})$/);

  if (!match) {
    return null;
  }

  const hour = Number(match[1]);
  const minute = Number(match[2]);

  if (hour < 1 || hour > HOURS_IN_HALF_DAY || minute > MAX_MINUTE) {
    return null;
  }

  return { hour, minute };
};

export interface DateTimePickerProps {
  id: string;
  /** ISO string, or null when nothing is selected. */
  value?: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  maxDate?: Date;
  minDate?: Date;
  isError?: boolean;
  errorText?: string;
  disabled?: boolean;
  className?: string;
}

const DateTimePicker = (props: DateTimePickerProps) => {
  const {
    id,
    value,
    onChange,
    placeholder,
    maxDate,
    minDate,
    isError,
    errorText,
    disabled,
    className,
  } = props;

  const selectedDate = getDateObject(value ?? null);

  const timeParts = useMemo<TimeParts>(() => {
    const date = getDateObject(value ?? null);

    if (!date) {
      return { hour: HOURS_IN_HALF_DAY, minute: 0, meridiem: MERIDIEM_AM };
    }

    const hours = date.getHours();

    return {
      hour:
        hours % HOURS_IN_HALF_DAY === 0
          ? HOURS_IN_HALF_DAY
          : hours % HOURS_IN_HALF_DAY,
      minute: date.getMinutes(),
      meridiem: hours < HOURS_IN_HALF_DAY ? MERIDIEM_AM : MERIDIEM_PM,
    };
  }, [value]);

  // Held only while the field has focus, so a half-typed "02:" is not parsed
  // and does not clobber the committed value.
  const [draftTime, setDraftTime] = useState<string | null>(null);
  const timeText = draftTime ?? formatTimeText(timeParts);

  // The day comes from the calendar and the time from the text/meridiem inputs,
  // so both sides recompose the single value on every change.
  const emitChange = (day: Date | null, time: TimeParts) => {
    if (!day) {
      onChange(null);

      return;
    }

    const composed = new Date(day);

    composed.setHours(to24Hour(time.hour, time.meridiem), time.minute, 0, 0);
    onChange(getDateString(composed));
  };

  const onTimeTextChange = (text: string) => {
    setDraftTime(text);

    const parsed = parseTimeText(text);

    if (parsed) {
      emitChange(selectedDate, { ...timeParts, ...parsed });
    }
  };

  // Dropping the draft snaps the field back to the committed value, so an
  // incomplete or out-of-range entry reverts rather than sticking.
  const onTimeTextBlur = () => setDraftTime(null);

  return (
    <div className={classnames(styles.dateTimeRow, className)}>
      <DatePicker
        id={id}
        maxDate={maxDate}
        minDate={minDate}
        placeholder={placeholder!}
        startDate={selectedDate}
        disabled={disabled}
        inputWrapperClassName={styles.dateTimeDate}
        isError={isError}
        errorText={errorText}
        setStartDate={(date) => emitChange(date, timeParts)}
      />
      <TextInput
        id={`${id}-time`}
        mask={TIME_MASK}
        value={timeText}
        placeholder="hh:mm"
        disabled={disabled || !selectedDate}
        wrapperClassName={styles.dateTimeText}
        onChange={(_event, text) => onTimeTextChange(text)}
        onBlur={onTimeTextBlur}
      />
      <SelectInput
        id={`${id}-meridiem`}
        options={MERIDIEM_OPTIONS}
        value={timeParts.meridiem}
        disabled={disabled || !selectedDate}
        wrapperClassName={styles.dateTimeSelect}
        onChange={(event, meridiem) => {
          event.preventDefault();
          emitChange(selectedDate, { ...timeParts, meridiem: String(meridiem) });
        }}
      />
    </div>
  );
};

export default DateTimePicker;
